import { useState, useEffect } from 'react';
import { BigNumber, ContractTransaction, ethers } from 'ethers';
import { useWallet } from 'components/WalletContext';
import { getEtherscanLink } from 'utils/helpers';
import { EulerBeatsV2, EulerBeatsV2Factory } from '../contracts/enigma';
import { DROP_CONFIG } from 'components/Drops/dropConfig';
import { checkDeployment } from 'utils/contractHelpers';
import { getStatus } from 'utils/legacyEbService';
import useLegacyIsRightNetwork from './useLegacyIsRightNetwork';

const MINT_GAS_LIMIT = 220000;
const BURN_GAS_LIMIT = 200000;

const useV2Contracts = () => {
  const [loading, setLoading] = useState(false);
  const { web3Provider, notify, address } = useWallet();
  const isRightNetwork = useLegacyIsRightNetwork();

  const [contract, setContract] = useState<EulerBeatsV2>();
  const [contractsReady, setContractsReady] = useState<boolean>(false);

  const [printIsEnabled, setPrintIsEnabled] = useState<boolean>(false);
  const [burnIsEnabled, setBurnIsEnabled] = useState<boolean>(false);

  const contractAddress = DROP_CONFIG.enigma.contractAddress;

  useEffect(() => {
    getStatus().then(status => {
      if (status.enigma) {
        const { mintPrintEnabled, burnPrintEnabled } = status.enigma;
        setPrintIsEnabled(mintPrintEnabled);
        setBurnIsEnabled(burnPrintEnabled);
      }
    });
  }, []);

  // SETUP CONTRACTS
  useEffect(() => {
    async function loadContracts() {
      setLoading(true);

      try {
        if (web3Provider && contractAddress) {
          console.log('Loading V2 contracts');
          const isContract = await checkDeployment(web3Provider, contractAddress);
          if (!isContract) throw Error('The provided enigma address is not a contract');

          const eulerBeatsV2 = EulerBeatsV2Factory.connect(
            contractAddress,
            web3Provider.getSigner()
          );
          setContract(eulerBeatsV2);

          const printingEnabled = await isPrintingEnabled(eulerBeatsV2);
          const burningEnabled = await isBurningEnabled(eulerBeatsV2);

          setPrintIsEnabled(printingEnabled);
          setBurnIsEnabled(burningEnabled);

          setContractsReady(true);
        }
      } catch (e) {
        console.log('ERROR LOADING V2 CONTRACTS:', e);
      } finally {
        setLoading(false);
      }
    }

    loadContracts();
  }, [web3Provider, contractAddress]);

  // ===============================================================================================
  // HELPERS
  // ===============================================================================================
  function checkContractAndNetwork() {
    if (!isRightNetwork) throw new Error('Wrong network');
    if (!contract) throw new Error(`Contract EulerBeatsV2 not setup yet`);
    console.log('contract', contract.address);
  }

  function getMinimumSupplyForBurn(printsSupply: BigNumber, allowedSlippage: number) {
    if (printsSupply.toNumber() - allowedSlippage <= 1) {
      return ethers.BigNumber.from(1);
    } else {
      return printsSupply.sub(allowedSlippage);
    }
  }

  // ===============================================================================================
  // GETTERS
  // ===============================================================================================
  async function getBalanceOf(account: string, tokenId: string) {
    // checkContractAndNetwork();
    const supply = await contract!.balanceOf(account, tokenId);
    return supply.toString();
  }

  async function getBalanceOfBatch(accounts: string[], tokenIds: string[]) {
    checkContractAndNetwork();

    if (accounts.length === 1 && tokenIds.length > 1) {
      console.log('Getting balances for address', accounts[0]);
      accounts = tokenIds.map(i => accounts[0]);
    }

    return await contract!.balanceOfBatch(accounts, tokenIds);
  }

  async function isOwner(account: string, tokenId: string) {
    // checkContractAndNetwork();
    const supply = await getBalanceOf(account, tokenId);
    return parseInt(supply) > 0;
  }

  // GET PRICE WITH SLIPPAGE
  async function getPriceWithSlippage(seed: string, allowedSlippage: number | string) {
    checkContractAndNetwork();
    if (!seed) throw new Error('Missing seed');

    if (typeof allowedSlippage === 'string') {
      return ethers.utils.parseEther(allowedSlippage);
    }

    const printsSupply = await contract!.seedToPrintsSupply(seed);
    const allowedPrintsSupply = printsSupply.add(allowedSlippage);
    const maxPrintPrice = await contract!.getPrintPrice(allowedPrintsSupply.add('1'));

    return maxPrintPrice;
  }

  // GET BURN PRICE WITH SLIPPAGE
  async function getBurnPriceWithSlippage(seed: string, allowedSlippage: number) {
    // checkContractAndNetwork();
    if (!seed) throw new Error('Missing seed');

    const printsSupply = await contract!.seedToPrintsSupply(seed);

    const allowedPrintsSupply = getMinimumSupplyForBurn(printsSupply, allowedSlippage);
    const actualBurnPrice = await contract!.getBurnPrice(allowedPrintsSupply);
    return actualBurnPrice;
  }

  async function isPrintingEnabled(contract: EulerBeatsV2) {
    const isEnabled = await contract.mintPrintEnabled();
    console.log('Printing is enabled:', isEnabled);
    return isEnabled;
  }

  async function isBurningEnabled(contract: EulerBeatsV2) {
    const isEnabled = await contract.burnPrintEnabled();
    console.log('Burning is enabled:', isEnabled);
    return isEnabled;
  }

  function handleEmitter(res: ContractTransaction, cb: any) {
    const { emitter } = notify.hash(res.hash);
    // const link = getEtherscanLink(res.hash);

    // note: could just do UI mode....
    // const { update, dismiss } = notify.notification({
    //   eventCode: 'txSent',
    //   type: 'pending',
    //   message: 'Your transaction has started',
    //   autoDismiss: 5000,
    // });

    // emitter.off('txRequest'); // Transaction has been initiated and is waiting approval from the user
    // emitter.off('txRepeat'); // Transaction has the same value and to address, so may be a repeat transaction
    // emitter.off('txAwaitingApproval'); // A previous transaction is awaiting approval
    // emitter.off('txConfirmReminder'); // Transaction has not been confirmed after timeout
    // emitter.off('txStallPending'); // Transaction has not been received in the mempool after timeout
    // emitter.off('txStallConfirmed'); // Transaction has been in the mempool for longer than the timeout
    // emitter.off('txError'); // An error has occurred initiating the transaction
    // emitter.off('txSendFail'); // The user rejected the transaction
    // emitter.off('txUnderpriced'); // The gas was set too low for the transaction to complete
    // // emitter.off('txSent'); // Transaction has been sent to the network

    // emitter.off('txPool'); // Transaction was detected in the "pending" area of the mempool and is eligible for inclusion in a block
    // emitter.off('txStuck'); // Transaction was detected in the "queued" area of the mempool and is not eligible for inclusion in a block
    // // emitter.off('txConfirmed'); // Transaction has been mined
    // // emitter.off('txFailed'); // Transaction has failed
    // emitter.off('txSpeedUp'); // A new transaction has been submitted with the same nonce and a higher gas price, replacing the original transaction
    // emitter.off('txCancel'); // A new transaction has been submitted with the same nonce, a higher gas price, a value of zero and sent to an external address (not a contract)
    // emitter.off('txDropped'); // Transaction was dropped from the mempool without being added to a block. NOTE: Transaction could reappear in mempool.

    emitter.on('txConfirmed', (tx: any) => {
      console.log('tx:', tx);
      if (tx.status === 'confirmed') {
        const link = getEtherscanLink(tx.hash);
        // Triggers success modal
        cb({ link, ...tx });
      }
      return {
        type: 'success',
        message:
          'Your transaction has succeeded. The UI may take a few moments to reflect these changes.',
        autoDismiss: 10000,
      };
    });

    // emitter.on('txSent', () => {});
    // emitter.on('all', () => false);
  }

  // ===============================================================================================
  // TRANSACTIONS
  // ===============================================================================================
  async function createPrint(
    originalId: string,
    originalOwner: string,
    allowedSlippage: number | string,
    onWrongOwner: any,
    cb: any
  ) {
    checkContractAndNetwork();
    const isEnabled = await isPrintingEnabled(contract!);
    if (!isEnabled) throw new Error('printing is disabled');

    if (!originalId) throw new Error('missing originalId');

    const maxPrintPrice = await getPriceWithSlippage(originalId, allowedSlippage);

    console.log(`Tokenid : ${originalId} Original owner: ${originalOwner}`);

    // Check that originalOwner is correct
    const originalOwnerBalance = await contract!.balanceOf(originalOwner, originalId);

    // This means the token was recently tranferred and our api doesn't have the updated address yet
    if (originalOwnerBalance.toString() === '0') {
      onWrongOwner(originalId);
      return;
    }

    const res = await contract!.mintPrint(originalId, originalOwner, {
      value: maxPrintPrice!.toString(),
      gasLimit: MINT_GAS_LIMIT,
    });
    handleEmitter(res, cb);
  }

  // BURN PRINT
  async function burnPrint(originalId: string, allowedSlippage: number, cb: any) {
    checkContractAndNetwork();
    const isEnabled = await isBurningEnabled(contract!);
    if (!isEnabled) throw new Error('burning is disabled');

    if (!originalId) throw new Error('Missing original id');

    const printsSupply = await contract!.seedToPrintsSupply(originalId);
    const minimumSupply = getMinimumSupplyForBurn(printsSupply, allowedSlippage);

    const res = await contract!.burnPrint(originalId, minimumSupply, {
      gasLimit: BURN_GAS_LIMIT,
    });
    handleEmitter(res, cb);
  }

  // Set approval
  async function approve(operator: string, cb: any) {
    checkContractAndNetwork();

    if (!address) return;

    // check approval
    const approved = await checkApproval(address!, operator);

    if (approved) {
      return cb(true);
    }

    console.log(`Approving ${operator}`);
    const res = await contract!.setApprovalForAll(operator, true);
    handleEmitter(res, cb);
  }

  // Check approval
  async function checkApproval(account: string, operator: string) {
    checkContractAndNetwork();

    const approved = await contract!.isApprovedForAll(account, operator);
    return approved;
  }

  return {
    burnPrint,
    createPrint,
    getPriceWithSlippage,
    getBurnPriceWithSlippage,
    contractsReady,
    getBalanceOf,
    getBalanceOfBatch,
    isOwner,
    contract,
    checkContractAndNetwork,
    burnIsEnabled,
    printIsEnabled,
    loading,
    approve,
    checkApproval,
  };
};

export default useV2Contracts;
